import { inject, Injectable } from '@angular/core';
import { TuiToastService } from '@taiga-ui/kit/components/toast';
import { friendlyErrorMessage } from './friendly-error';

export type ToastType = 'success' | 'error';

const AUTO_DISMISS_MS = 4000;

const ICON: Record<ToastType, string> = {
  success: '@tui.circle-check',
  error: '@tui.circle-x',
};

const APPEARANCE: Record<ToastType, string> = {
  success: 'positive',
  error: 'negative',
};

/**
 * Thin facade over Taiga's toast portal, kept so the ~270 call sites stay on
 * plain `success()`/`error()` and don't have to know about appearances, icon
 * names or the portal's subscribe-to-show contract.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly toasts = inject(TuiToastService);

  success(message: string): void {
    this.show(message, 'success');
  }

  /**
   * A technical message (supabase-js, the database, a network failure) is
   * shown as a plain, translated sentence instead — see friendly-error.ts.
   * Messages written for people pass through as they are.
   */
  error(message: string): void {
    this.show(friendlyErrorMessage(message), 'error');
  }

  private show(message: string, type: ToastType): void {
    // `open()` is cold — nothing renders until it is subscribed to. It
    // completes by itself once the toast closes, so there is nothing to tear
    // down and no leak in firing and forgetting.
    this.toasts
      .open(escapeHtml(message), {
        appearance: APPEARANCE[type],
        data: ICON[type],
        autoClose: AUTO_DISMISS_MS,
        // Kept, but they do NOT position anything today: this version of
        // TuiToast binds only [attr.data-appearance] on its host, so `block`
        // and `inline` never reach the DOM and its stylesheet's
        // [data-inline=...] rules never match. The actual top-right placement
        // is a `tui-toast:not(._mobile)` override in styles.css, which has to
        // fight both a grid layout and a doubled host selector — change it
        // there. These stay so the intent is declared at the call site, and so
        // the behaviour is already right if Taiga starts honouring them.
        block: 'start',
        inline: 'end',
      })
      .subscribe();
  }
}

/**
 * Taiga renders toast content through `[innerHTML]`, but every message we pass
 * is plain text — often with a user-supplied clinic or member name spliced in
 * by the translator. Angular's sanitizer already blocks script injection; this
 * additionally stops a name like `Smith <Dental>` from being swallowed as a
 * tag, matching how the messages used to render as interpolated text.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
